import { Color } from '@persona-id/design-tokens';
import styled from 'styled-components';

import PageSelector from 'dashboard/components/Pagination/PageSelector';
import PageSizeSelector from 'dashboard/components/Pagination/PageSizeSelector';
import { DEFAULT_PAGE_SIZE_OPTIONS } from 'dashboard/lib/constants/pagination';
import { PaginationParams } from 'lib/interfaces/api';

// NB: this is configured in config/initializers/kaminari.rb
const MAX_PAGES = 100;

const Container = styled('nav')`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const PaginationInfo = styled('div')`
  display: flex;
  align-items: center;

  color: ${Color.TextPrimary};
`;

interface PaginationProps {
  pageSize?: number;
  pageSizeOptions?: number[];
  paginationParams?: PaginationParams;
  handlePageSizeClick: (pageSize: number) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  isLoading?: boolean;
}

const Pagination = ({
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  paginationParams,
  handlePageSizeClick,
  handlePageLinkClick,
  isLoading,
}: PaginationProps) => {
  if (!paginationParams || !pageSize) {
    return <div />;
  }

  const {
    'current-page': currentPage,
    'total-count': totalCount,
    'total-pages': totalPages,
    'lower-bound': lowerBound,
  } = paginationParams;

  // If the total number of pages is the same as MAX_PAGES, we guess that the total number of
  // items might be limited by max pages, and that the total could change based on page size.
  // To limit user confusion here, we use 'many' instead of a specific number.
  const totalCountDisplay = lowerBound || totalPages >= MAX_PAGES ? 'many' : totalCount;

  const lastItemIndex = lowerBound
    ? currentPage * pageSize
    : Math.min(currentPage * pageSize, totalCount);
  const firstItemIndex = pageSize * (currentPage - 1) + 1;

  return (
    <Container role='navigation' aria-label='pagination'>
      <PageSizeSelector
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        handlePageSizeClick={handlePageSizeClick}
      />
      {totalCount > 0 && (
        <>
          <div>
            Showing {firstItemIndex}-{lastItemIndex} of {totalCountDisplay} items
          </div>
          <PaginationInfo>
            <PageSelector
              paginationParams={paginationParams}
              handlePageLinkClick={handlePageLinkClick}
              isLoading={isLoading}
            />
          </PaginationInfo>
        </>
      )}
    </Container>
  );
};

export default Pagination;
